/**
 * Onboarding Component
 * Tutorial for first-time users
 */

import React, { useState } from 'react';
import './Onboarding.css';
import { ArrowRight, Check } from 'lucide-react';

const Onboarding = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: 'Welcome to GONGFU',
      content: (
        <div>
          <p>This is an interactive training platform for product managers. You'll practice real PM scenarios in a safe, game-like environment.</p>
          <div className="feature-list">
            <div className="feature-item">
              <Check size={20} />
              <span>Chat with AI stakeholders</span>
            </div>
            <div className="feature-item">
              <Check size={20} />
              <span>Use workspace tools for PRDs and roadmaps</span>
            </div>
            <div className="feature-item">
              <Check size={20} />
              <span>Get guidance from an AI mentor</span>
            </div>
            <div className="feature-item">
              <Check size={20} />
              <span>Track your progress and skills</span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'How It Works',
      content: (
        <div>
          <p>Each scenario simulates a real product management challenge:</p>
          <ol className="steps-list">
            <li><strong>Read the situation</strong> - Understand the context and your goals</li>
            <li><strong>Complete tasks</strong> - Work through realistic PM tasks</li>
            <li><strong>Chat with stakeholders</strong> - Communicate with AI-powered team members</li>
            <li><strong>Use tools</strong> - Create PRDs, roadmaps, and upload files</li>
            <li><strong>Get feedback</strong> - Receive scores and learn from your decisions</li>
          </ol>
        </div>
      )
    },
    {
      title: 'SaaS Product Track',
      content: (
        <div>
          <div className="track-card">
            <h3>🚀 SaaS Track</h3>
            <p>Learn traditional SaaS product management: discovery, sprint planning, design collaboration, development, and launch.</p>
          </div>
          <p className="hint">Start with "Discovery & Definition" to begin your PM journey.</p>
        </div>
      )
    },
    {
      title: 'Your AI Mentor',
      content: (
        <div>
          <p>You'll have access to an expert PM mentor with:</p>
          <ul className="mentor-features">
            <li><strong>Guidance Mode:</strong> Get helpful advice when you're stuck</li>
            <li><strong>Challenge Mode:</strong> Question your assumptions with Socratic method</li>
            <li><strong>Review Mode:</strong> Get feedback on your completed work</li>
            <li><strong>Framework Mode:</strong> Learn PM frameworks like RICE, Jobs-to-be-Done, and more</li>
          </ul>
        </div>
      )
    },
    {
      title: 'Ready to Start?',
      content: (
        <div>
          <p>You're all set! We'll take you to your first scenario now.</p>
          <p className="hint">Remember: There's no right or wrong answer. The goal is to practice thinking like a PM and learn from each scenario.</p>
          <p className="hint">You'll start with "Discovery & Definition" - your first PM challenge!</p>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <div className="onboarding">
      <div className="onboarding-container">
        <div className="onboarding-header">
          <h1>{steps[currentStep].title}</h1>
          <button className="skip-button" onClick={handleSkip}>
            Skip
          </button>
        </div>

        <div className="onboarding-content">
          {steps[currentStep].content}
        </div>

        <div className="onboarding-footer">
          <div className="progress-dots">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              />
            ))}
          </div>

          <button className="next-button" onClick={handleNext}>
            {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
